// mediaFrame.ts — aspect policy + the shared visual primitives for media frames.
//
// THE POLICY: frame, don't crop. An image is shown at its true aspect ratio and made to fit
// the height budget by NARROWING, not by cropping. The only two exceptions are announced to
// the reader with a mono chip, because truncation is information rather than decoration.

import { css } from 'styled-components';

/** 1:2 — the tallest honest frame. Below this the image is top-anchored and clipped. */
export const AR_FLOOR = 0.5;
/** 3:1 — the widest honest frame. Above this the image is contained with bars. */
export const AR_CEIL = 3.0;
/** 3:2 — the box reserved before load when the API gave us no dimensions. */
export const AR_RESERVE = 1.5;

export type FitMode = 'contain' | 'cover';

export interface FitResolution {
  /** The frame's aspect ratio (the intrinsic ratio, clamped into the honest band). */
  ar: number;
  fit: FitMode;
  objectPosition: string;
  /** Mono chip text when the frame had to deviate from the true ratio; null otherwise. */
  chip: string | null;
}

/**
 * Resolve the frame geometry for an intrinsic ratio.
 *
 * Inside the band the frame ratio EQUALS the image ratio, so `cover` is a no-op that only
 * absorbs sub-pixel rounding — nothing is cropped.
 */
export function resolveFit(ratio: number): FitResolution {
  if (!Number.isFinite(ratio) || ratio <= 0) {
    return { ar: AR_RESERVE, fit: 'cover', objectPosition: '50% 50%', chip: null };
  }
  if (ratio < AR_FLOOR) {
    // Very tall (long screenshots). Anchor to the TOP, where the content starts.
    return { ar: AR_FLOOR, fit: 'cover', objectPosition: '50% 0%', chip: 'Full image' };
  }
  if (ratio > AR_CEIL) {
    // Ultra-wide panorama. Contained, so nothing is lost; the bars are the card surface.
    return { ar: AR_CEIL, fit: 'contain', objectPosition: '50% 50%', chip: 'Pano' };
  }
  return { ar: ratio, fit: 'cover', objectPosition: '50% 50%', chip: null };
}

/**
 * Mirrored corner brackets — the "this opens" affordance.
 *
 * Panel owns top-left + bottom-right; media deliberately takes the OPPOSITE diagonal so a
 * card and its media read as a matched pair rather than a repeat. The
 * `calc(14px + radius.sm)` at `-1px` offsets formula is lifted from Panel, which is the
 * proven way brackets stay attached at radius 0 AND at radius 48.
 *
 * The host element must NOT set `overflow: hidden` or the -1px offsets get clipped and the
 * affordance silently disappears — that is why the frame clips on an inner element.
 */
/* 1.25.00: the bracket pseudo-elements are gone. Accent corner ticks appearing
   over a member's photograph on hover were sci-fi ornament on user content —
   the one place the app should draw the least. The selectors stay as inert
   no-ops so the mixin's call sites need no edit; the hover affordance is the
   frame's own hairline. */
export const brackets = css`
  &::before,
  &::after {
    content: none;
  }
  &::before {
    top: -1px;
    right: -1px;
    border-top-right-radius: ${({ theme }) => theme.radius.sm};
  }
  &::after {
    bottom: -1px;
    left: -1px;
    border-bottom-left-radius: ${({ theme }) => theme.radius.sm};
  }
  &:hover::before,
  &:hover::after,
  &:focus-visible::before,
  &:focus-visible::after {
    opacity: 1;
  }

  @media (prefers-reduced-motion: reduce) {
    &::before,
    &::after {
      transition: none;
    }
  }
`;
